import { Component, forwardRef, Inject, OnInit } from '@angular/core';
import { Charge } from '../../../../models/entities/charge';
import { ChargesService } from '../../../../services/charges/charges.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LegFiJwtService } from 'app/services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from 'app/services/auth/jwt-legfi-claims.model';
import { PaymentGroupsService } from 'app/services/payment/payment-groups.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput, ConfirmActionDialogResult } from '../confirm-action-dialog/confirm-action-dialog.component';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { ConfirmWithInputDialogComponent, ConfirmWithInputDialogInput, ConfirmWithInputDialogResult } from '../confirm-with-input-dialog/confirm-with-input-dialog.component';
import { SharedModule } from '../../../shared/shared.module';
import { SingleChargeOptionsComponent } from '../single-charge-options/single-charge-options.component';
import { LFDropdownModule } from '../../../shared/dropdown/dropdown.module';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MultiChargeOptionsComponent } from '../multi-charge-options/multi-charge-options.component';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { SuperStarComponent } from '../super-star/super-star.component';
import { OrganizationV2DTO, OrganizationV2Service } from '../../../../services/organization/organization-v2.service';
import { LedgerEntriesService } from '../../../../services/accounting/ledger-entries.service';
import { GroupByPipe } from '../../../../pipes/collection/group-by.pipe';
import { SumPipe } from '../../../../pipes/collection/sum.pipe';

export interface ChargeDetailPopupInput
{
    chargeId: number;
}

@UntilDestroy()
@Component({
    selector: 'lf-charge-detail-popup',
    styleUrls: ['./charge-detail-popup.scss'],
    templateUrl: './charge-detail-popup.component.html',
    standalone: true,
    imports: [
        SharedModule,
        forwardRef(() => SingleChargeOptionsComponent),
        LFDropdownModule,
        MatMenuModule,
        MatCheckboxModule,
        MultiChargeOptionsComponent,
        LegfiCurrencyPipe,
        LegfiDatePipe,
        SuperStarComponent,
        MatLegacyDialogModule,
        GroupByPipe,
        SumPipe,
    ],
})
export class ChargeDetailPopupComponent implements OnInit
{
    chargeId: number = null;
    charge: Charge = null;

    jwt: JwtLegFiClaims;
    hasWriteAccess = false;
    hasPaymentsWrite = false;

    selectedChildCharges: SelectionModel<Charge> = new SelectionModel<Charge>(true);
    accrualAccounting = false;
    hasAccountingV2 = false;

    constructor(
            private _chargesService: ChargesService,
            private _paymentGroupsService: PaymentGroupsService,
            private _ledgerEntriesService: LedgerEntriesService,
            private _matDialogRef: MatDialogRef<ChargeDetailPopupComponent>,
            private _growler: GrowlerService,
            private _dialog: MatDialog,
            private _organizationService: OrganizationV2Service,
            @Inject(MAT_DIALOG_DATA) private data: ChargeDetailPopupInput,
    ) {
    }

    ngOnInit() {
        this.chargeId = this.data.chargeId;
        this.jwt = LegFiJwtService.read();
        this.hasAccountingV2 = LegFiJwtService.featureEnabled('accounting-v2');
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission('charges', true);
        this.hasPaymentsWrite = LegFiJwtService.doesUserHaveModulePermission('payments', true);
        this._organizationService.getOrganizationById(this.jwt.orgId)
                .pipe(untilDestroyed(this))
                .subscribe((org: OrganizationV2DTO) => {
                    this.accrualAccounting = !org.preferences.useCashAccounting;
                });

        if (this.chargeId) {
            this.getCharge();
        }

        this._chargesService.chargesArchived$.asObservable()
                .pipe(untilDestroyed(this))
                .subscribe({next: () => this.switchCharge(this.chargeId)});
    }

    toggleAllChildren() {
        if (this.selectedChildCharges.isSelected(this.charge.children[0])) {
            this.selectedChildCharges.clear();
        } else {
            this.selectedChildCharges.select(...this.charge.children);
        }

    }

    removeLateFee() {
        const requestBody: { charge: any, chargeIds: number[] } = {
            charge: {
                removeLateFees: true,
            },
            chargeIds: [this.chargeId],
        };

        this._chargesService.updateCharges(null, requestBody).subscribe({
            next: () => {
                this._growler.success(
                        'Late Fee Removed',
                        'The charge is now refreshing.',
                );
                this.getCharge();
            },
            error: () => {
                this._growler.error('Oops...', 'Unable to remove late fees.');
            },
        });
    }

    switchCharge(newId: number) {
        this.chargeId = newId;
        this.charge = null;
        this.getCharge();
    }

    creditPaymentItem(paymentItem: any) {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: {
                title: 'Issue a credit for this payment?',
                content: 'This will unlink this payment from this charge and issue a credit in the same amount. Outstanding charges will be paid with this credit immediately.',
                confirmButton: 'Create Credit',
            },
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result?.success) {
                    paymentItem.refunded = true;
                    this._paymentGroupsService.issueCreditForPaymentItem(paymentItem.paymentItemId)
                            .subscribe({
                                next: () => {
                                    this._growler.success('Credit Issued', 'The charge is now refreshing.');
                                    this.getCharge();
                                },
                            });
                }
            },
        });
    }

    updateChargeBalance() {
        this._chargesService.updateChargeBalance(null, this.chargeId).subscribe({
            next: () => {
                this.getCharge();
                this._growler.success('Charge Balance Updated', 'The charges balance was updated');
            },
            error: () => {
                this._growler.error('Error', 'The charges balance was not updated');
            },
        });
    }

    getCharge() {
        this._chargesService
                .getIssuedCharge(this.chargeId, true)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (charge: Charge) => {
                        this.charge = charge;
                    },
                    error: () => {
                        this._growler.error('Error Loading Charge', 'We could not find this charge\'s details');
                    },
                });
    }

    //noinspection JSMethodCanBeStatic
    clickGuard($event: any) {
        $event.stopPropagation();
    }

    forgiveCharge() {
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: <ConfirmWithInputDialogInput>{
                title: 'Forgive Invoice Balance',
                content: `Are you sure you want to forgive the remaining balance of the selected invoice?`,
                btnContent: 'Yes, forgive it',
                label: 'Reason for forgiveness',
                inputRequired: false,
            },
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result.success) {
                this._chargesService.forgiveCharges([this.charge.id], result.value).subscribe({
                    next: () => {
                        this._matDialogRef.close(true);
                        this._growler.success('Success', 'Unpaid balances of affected charges have been deleted.');
                    },
                    error: () => {
                        this._growler.oops('There was an issue forgiving your charges.');
                    },
                });
            }
        });
    }

    deleteLedgerBatch(batchId: string) {
        const data: ConfirmActionDialogInput = {
            title: 'Delete Ledger Batch?',
            content: 'This action is outside the regular flow of entries and should only be used to fix irreversible issues.',
            confirmButton: 'Delete Batch'
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data
        }).afterClosed().subscribe((result: ConfirmActionDialogResult) => {
            if (result?.success) {
                this._ledgerEntriesService.deleteBatch(this.jwt.orgId, batchId)
                        .subscribe({
                            next: () => {
                                this._growler.success('Success', 'Ledger batch deleted successfully.');
                                this.getCharge();
                            }
                        });
            }
        });
    }

    /**
     * Super users may edit paid and deleted charges, except a deleted (or forgiven) charge on
     * Accounting V2: its ledger entries are already reversed, so an edit would only leave the
     * invoice out of step with the ledger (SD-9353). The API rejects that edit as well.
     */
    get canEditCharge(): boolean {
        if (this.jwt.superUser) {
            return !this.charge.deletedAt || !this.hasAccountingV2;
        }

        return this.hasWriteAccess && !this.charge.paid && !this.charge.deletedAt;
    }

    editCharge() {
        this._matDialogRef.afterClosed().subscribe({
            next: () => {
                this._chargesService.editCharge$.next(this.charge.id);
            },
        });
        this._matDialogRef.close();
    }
}
